import React, { useContext, useEffect, useState } from "react";
import Layout from "../../CommonComponents/Layout/Layout";
import AppContext from "../../../context/AppContext";
import { SIDER_TABS } from "../../../utils/Constants";
import { API_ROUTES } from "../../../api/ApiRoutes";
import createPrivateAdminPanelInstance from "../../../api/PrivateAdminPanelInstance";
import { PlusOutlined } from "@ant-design/icons";
import { message } from "antd";

import SortingTable from "./CasinoRelated/SortingTable/SortingTable";
import CategoriesPopup from "./CategoriesPopup/CategoriesPopup";
import CollectionsPopup from "./CollectionsPopup/CollectionsPopup";
import AllGamesPopup from "./AllGamesPopup/AllGamesPopup";

import "./stylesCasinoFM.css";

function CasinoFM() {
    const token = JSON.parse(localStorage.getItem("token"));
    const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(token?.IdToken);
    const { setActiveSidebarTab } = useContext(AppContext);

    const [isCategoriesPopupVisible, setCategoriesPopupVisible] = useState(false);
    const [apiIsRunning, setApiIsRunning] = useState(false);
    const [isCollectionsPopupVisible, setCollectionsPopupVisible] = useState(false);
    const [isAllGamesPopupVisible, setAllGamesPopupVisible] = useState(false);
    const [removingGameData, setRemovingGameData] = useState(null);

    const [categories, setCategories] = useState([]);
    const [collections, setCollections] = useState([]);
    const [allGames, setAllGames] = useState([]);

    const [messageApi, contextHolder] = message.useMessage();

    useEffect(() => {
        setActiveSidebarTab(SIDER_TABS.CONFIGURATIONS, "Casino FM");
    }, [setActiveSidebarTab]);

    const getCasinoGames = async () => {
        setApiIsRunning(true);

        try {
            const response = await PrivateAdminPanelInstance.post(API_ROUTES.GET_CASINO_GAMES);
            const { data } = response.data;

            const categories = data.categories
                .map((category) => ({
                    ...category,
                    id: category.ename,
                }))
                .sort((a, b) => a.order - b.order);

            const collections = data.collections
                .map((collection) => ({
                    ...collection,
                    id: collection.ename,
                }))
                .sort((a, b) => a.order - b.order);

            setAllGames(data.all);
            setCategories(categories);
            setCollections(collections);
        } catch (error) {
            console.log("error getting casino games", error);
        } finally {
            setApiIsRunning(false);
        }
    };

    const changeCasinoEntityOrder = async ({ name, order, etype }) => {
        setApiIsRunning(true);
        const params = { name, order, etype };

        try {
            const res = await PrivateAdminPanelInstance.post(
                API_ROUTES.CHANGE_CASINO_ENTITY_ORDER,
                params
            );
            messageApi.success(res.data.message);
        } catch (error) {
            console.log("error changing casino entity order", error);
        } finally {
            setApiIsRunning(false);
        }
    };

    const toggleCasinoGameStatus = async ({ name, etype, provider, game_id }) => {
        const params = { name, etype, provider, game_id };

        try {
            const res = await PrivateAdminPanelInstance.post(
                API_ROUTES.TOGGLE_CASINO_GAME_STATUS,
                params
            );

            if (etype === "category") {
                setCategories((prevData) =>
                    prevData.map((category) => ({
                        ...category,
                        games: category.games.map((game) =>
                            game.game_id === game_id && name === category.ename
                                ? { ...game, is_active: !game.is_active }
                                : game
                        ),
                    }))
                );
            } else {
                setCollections((prevData) =>
                    prevData.map((collection) => ({
                        ...collection,
                        games: collection.games.map((game) =>
                            game.game_id === game_id && name === collection.ename
                                ? { ...game, is_active: !game.is_active }
                                : game
                        ),
                    }))
                );
            }

            messageApi.success(res.data.message);
        } catch (error) {
            messageApi.error("Error toggling game status");
            console.log("Toggle game status error", error);
        }
    };

    const removeGameFromCasinoEntity = async () => {
        const params = {
            name: removingGameData.name,
            game_id: removingGameData.game_id,
            provider: removingGameData.provider,
            etype: removingGameData.etype,
        };

        try {
            const res = await PrivateAdminPanelInstance.post(
                API_ROUTES.REMOVE_GAME_FROM_CASINO_ENTITY,
                params
            );

            if (removingGameData.etype === "category") {
                setCategories((prevData) => {
                    return prevData.map((category) => {
                        if (category.ename === removingGameData.name) {
                            return {
                                ...category,
                                games: category.games.filter(
                                    (game) => game.game_id !== removingGameData.game_id
                                ),
                            };
                        }
                        return category;
                    });
                });
            } else {
                setCollections((prevData) => {
                    return prevData.map((collection) => {
                        if (collection.ename === removingGameData.name) {
                            return {
                                ...collection,
                                games: collection.games.filter(
                                    (game) => game.game_id !== removingGameData.game_id
                                ),
                            };
                        }
                        return collection;
                    });
                });
            }

            messageApi.success(res.data.message);
        } catch (error) {
            messageApi.error("Error toggling game status");
            console.log("Toggle game status error", error);
        }
    };

    const addCasinoEntity = async ({ icon, name, etype }) => {
        const params = etype === "category" ? { icon, name, etype } : { name, etype };

        try {
            const res = await PrivateAdminPanelInstance.post(API_ROUTES.ADD_CASINO_ENTITY, params);

            if (etype === "category") {
                setCategories((prevData) => [
                    ...prevData,
                    { ename: name, icon, is_active: false, games: [] },
                ]);
            } else {
                setCollections((prevData) => [
                    ...prevData,
                    { ename: name, is_active: false, games: [] },
                ]);
            }

            messageApi.success(res.data.message);
        } catch (error) {
            messageApi.error("Error adding casino entity");
            console.log("Add casino entity error", error);
        }
    };

    const addGameInCasinoEntity = async (game) => {
        const params = {
            name: game.ename,
            game_id: game.game_id,
            provider: game.provider,
            etype: game.etype,
        };

        try {
            const res = await PrivateAdminPanelInstance.post(
                API_ROUTES.ADD_GAME_IN_CASINO_ENTITY,
                params
            );

            if (game.etype === "category") {
                setCategories((prevData) => {
                    return prevData.map((category) => {
                        if (category.ename === game.ename) {
                            return {
                                ...category,
                                games: [...category.games, { ...game, is_active: true }],
                            };
                        }
                        return category;
                    });
                });
            } else {
                setCollections((prevData) => {
                    return prevData.map((collection) => {
                        if (collection.ename === game.ename) {
                            return {
                                ...collection,
                                games: [...collection.games, { ...game, is_active: true }],
                            };
                        }
                        return collection;
                    });
                });
            }

            messageApi.success(res.data.message);
        } catch (error) {
            messageApi.error("Error adding game in casino entity");
            console.log("Error adding game in casino entity", error);
        }
    };

    const toggleCasinoEntityStatus = async ({ ename, etype }) => {
        const params = { name: ename, etype };

        try {
            const res = await PrivateAdminPanelInstance.post(
                API_ROUTES.TOGGLE_CASINO_ENTITY_STATUS,
                params
            );

            if (etype === "category") {
                setCategories((prevData) =>
                    prevData.map((category) =>
                        category.ename === ename
                            ? { ...category, is_active: !category.is_active }
                            : category
                    )
                );
            } else {
                setCollections((prevData) =>
                    prevData.map((collection) =>
                        collection.ename === ename
                            ? { ...collection, is_active: !collection.is_active }
                            : collection
                    )
                );
            }

            messageApi.success(res.data.message);
        } catch (error) {
            messageApi.error("Error toggling casino entity status");
            console.log("Error toggling casino entity status", error);
        }
    };

    const EditGameIcon = async ({ image, game_id, provider }) => {
        const params = { image, game_id, provider };

        try {
            const res = await PrivateAdminPanelInstance.post(
                API_ROUTES.UPDATE_CASINO_GAME_THUMB,
                params
            );

            setCategories((prevData) => {
                return prevData.map((category) => {
                    return {
                        ...category,
                        games: category.games.map((game) => {
                            if (game.game_id === game_id) {
                                return { ...game, image };
                            }
                            return game;
                        }),
                    };
                });
            });

            setCollections((prevData) => {
                return prevData.map((collection) => {
                    return {
                        ...collection,
                        games: collection.games.map((game) => {
                            if (game.game_id === game_id) {
                                return { ...game, image };
                            }
                            return game;
                        }),
                    };
                });
            });

            messageApi.success(res.data.message);
        } catch (error) {
            console.error("Error updating game icon:", error);
            messageApi.error("Failed to update game icon. Please try again.");
        }
    };

    useEffect(() => {
        getCasinoGames();
    }, []);

    return (
        <Layout name={SIDER_TABS.CONFIGURATIONS}>
            <div className="location-restrictions-table-container">
                {contextHolder}
                <div className="location-restrictions-table">
                    <CategoriesPopup
                        isVisible={isCategoriesPopupVisible}
                        onClosePressed={setCategoriesPopupVisible}
                        getEventCodesList={addCasinoEntity}
                    />

                    <CollectionsPopup
                        isVisible={isCollectionsPopupVisible}
                        onClosePressed={setCollectionsPopupVisible}
                        getEventCodesList={addCasinoEntity}
                    />

                    <AllGamesPopup
                        isVisible={isAllGamesPopupVisible}
                        onClosePressed={setAllGamesPopupVisible}
                        getEventCodesList={() => {}}
                    />

                    <div>
                        <div className="header-text-container">
                            <p className="header-text">Casino Feature Management</p>
                        </div>
                    </div>

                    <div className="tab-content-sportsbook-fm">
                        <div className="sorting-table-wrapper">
                            <div className="sorting-table-wrapper-header">
                                <h2>Casino</h2>
                                <button
                                    className="sorting-table-wrapper-header__button"
                                    onClick={() => setCategoriesPopupVisible(true)}
                                >
                                    <PlusOutlined />
                                    <span> Add</span>
                                </button>
                            </div>
                            <SortingTable
                                columns={["# Order", "Icon", "Category", "Game Name", " Status", "Activity"]}
                                data={categories}
                                setData={setCategories}
                                toggleStatus={toggleCasinoGameStatus}
                                removeGame={removeGameFromCasinoEntity}
                                setRemovingGameData={setRemovingGameData}
                                etype="category"
                                allGames={allGames}
                                addGameInCasinoEntity={addGameInCasinoEntity}
                                toggleCasinoEntityStatus={toggleCasinoEntityStatus}
                                changeCasinoEntityOrder={changeCasinoEntityOrder}
                                EditGameIcon={EditGameIcon}
                                rows={["order", "icon", "ename", "blank", "is_active"]}
                            />
                        </div>

                        <div className="sorting-table-wrapper">
                            <div className="sorting-table-wrapper-header">
                                <h2>Collections</h2>
                                <button
                                    className="sorting-table-wrapper-header__button"
                                    onClick={() => setCollectionsPopupVisible(true)}
                                >
                                    <PlusOutlined />
                                    <span> Add</span>
                                </button>
                            </div>
                            <SortingTable
                                columns={["# Order", "Icon", "Collections", "Game Name", " Status", "Activity"]}
                                data={collections}
                                setData={setCollections}
                                toggleStatus={toggleCasinoGameStatus}
                                removeGame={removeGameFromCasinoEntity}
                                setRemovingGameData={setRemovingGameData}
                                etype="collection"
                                allGames={allGames}
                                addGameInCasinoEntity={addGameInCasinoEntity}
                                toggleCasinoEntityStatus={toggleCasinoEntityStatus}
                                changeCasinoEntityOrder={changeCasinoEntityOrder}
                                rows={["order", "icon", "ename", "blank", "is_active"]}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

export default CasinoFM;
